import { raw, type Credential } from '../api/client';

/** A PDF fetched with the caller's credential, held as an object URL. */
export interface FetchedPdf {
  url: string;
  name: string;
}

/**
 * Fetches a PDF the API answers with. The routes need the caller's
 * credential, so a plain link cannot fetch them: the bytes come through
 * {@link raw} and are kept as an object URL under the server's file name.
 * The caller revokes the URL when it is done with it.
 */
export async function fetchPdf(
  path: string,
  opts: { orgId: string; credential: Credential | null },
): Promise<FetchedPdf> {
  const response = await raw(path, opts);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'arbeitsblatt.pdf';
  return { url: URL.createObjectURL(await response.blob()), name };
}

/** Hands a fetched PDF to the browser to save under its name. */
export function savePdf({ url, name }: FetchedPdf): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Saves a PDF the API answers with. */
export async function downloadPdf(
  path: string,
  opts: { orgId: string; credential: Credential | null },
): Promise<void> {
  const pdf = await fetchPdf(path, opts);
  savePdf(pdf);
  setTimeout(() => URL.revokeObjectURL(pdf.url), 10_000);
}

/**
 * The path that asks the server to show a PDF rather than save it. The bytes
 * are the same either way; the console asks for them inline when it means to
 * show them, so the request says what it is for.
 */
export function inlinePath(path: string): string {
  return path + (path.includes('?') ? '&' : '?') + 'inline=true';
}
